import { Component, Injector, Input, OnInit, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BaseModalComponent, EnumValues } from '@card-software/library';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { CustomsData } from '../../../models/customs-data';
import {
  DucaInformationFormGroup,
  ExporterFormGroup,
  Shipment,
  SupportingDocumentIdsFormGroup,
  TotalValuesFormGroup,
  TransporterFormGroup,
  TransportFormGroup,
} from '../../../models/shipment/shipment';
import { ScrappinProxyService } from '../../../proxies/order/scrappin.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { TranslateWithFallbackPipe } from '../../../pipes/translate-with-fallback.pipe';
import { setOptionsForPrimeNg } from '../../../PrimeNG/utils';
import { ApiResponse } from '@card-software/nexus-utilities';

@Component({
  standalone: true,
  selector: 'app-duca-modal',
  templateUrl: './duca-modal.component.html',
  styleUrl: './duca-modal.component.scss',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    TooltipModule,
    ButtonModule,
    TableModule,
    TranslateWithFallbackPipe,
    SelectModule,
  ],
})
export class DUCAModalComponent extends BaseModalComponent<unknown> implements OnInit {
  @Input() shipment: Shipment | null = null;
  @Input() exporterForm: FormGroup<ExporterFormGroup> | null = null;
  @Input() ducaInfoForm: FormGroup<DucaInformationFormGroup> | null = null;
  @Input() transportForm: FormGroup<TransportFormGroup> | null = null;
  @Input() transporterForm: FormGroup<TransporterFormGroup> | null = null;
  @Input() totalValuesForm: FormGroup<TotalValuesFormGroup> | null = null;
  @Input() supportingDocsForm: FormGroup<SupportingDocumentIdsFormGroup> | null = null;
  @Input() canEdit = false;
  public url = new FormControl('');
  public customsData: CustomsData | null = null;
  public isLoadingCustomsData = false;
  public isDataPopulated = false;
  public originalFormValues: Record<string, unknown> | null = null;
  public unitOfMeasurementEnums: EnumValues = {};
  setOptionsForPrimeNg = setOptionsForPrimeNg;
  selectedUomLabel = '';

  private scrappingService = inject(ScrappinProxyService);
  private enumService = inject(EnumService);

  constructor(injector: Injector) {
    super(injector);
    effect(() => {
      this.unitOfMeasurementEnums = this.enumService.getEnum('UnitOfMeasure', 'itemEnums')?.values;
    });
  }

  ngOnInit(): void {
    if (!this.canEdit) {
      this.exporterForm?.disable();
      this.ducaInfoForm?.disable();
      this.transportForm?.disable();
      this.transporterForm?.disable();
      this.totalValuesForm?.disable();
      this.supportingDocsForm?.disable();
    }
  }

  public fetchDataFromUrl(): void {
    this.isLoadingCustomsData = true;
    const url = this.url.value;

    if (!url) {
      console.error('URL is required to load customs data.');
      this.isLoadingCustomsData = false;
      return;
    }

    this.scrappingService.getScrappin(url).subscribe({
      next: (data: ApiResponse<CustomsData>) => {
        this.customsData = data.data;
        this.isLoadingCustomsData = false;
      },
      error: (error) => {
        console.error('Error loading customs data:', error);
        this.isLoadingCustomsData = false;
      },
    });
  }

  public toggleDataPopulation(): void {
    if (!this.isDataPopulated) {
      this.originalFormValues = {
        exporter: this.exporterForm ? this.exporterForm.getRawValue() : null,
        ducaInfo: this.ducaInfoForm?.getRawValue() ?? null,
        transport: this.transportForm?.getRawValue() ?? null,
        transporter: this.transporterForm?.getRawValue() ?? null,
        totalValues: this.totalValuesForm?.getRawValue() ?? null,
      };

      if (this.customsData) {
        this.populateCustomsDataForm(this.customsData);
      }

      this.isDataPopulated = true;
    } else {
      if (!this.originalFormValues) {
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.exporterForm?.patchValue(this.originalFormValues['exporter'] as any);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.ducaInfoForm?.patchValue(this.originalFormValues['ducaInfo'] as any);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.transportForm?.patchValue(this.originalFormValues['transport'] as any);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.transporterForm?.patchValue(this.originalFormValues['transporter'] as any);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.totalValuesForm?.patchValue(this.originalFormValues['totalValues'] as any);

      this.isDataPopulated = false;
    }
  }

  private populateCustomsDataForm(data: CustomsData): void {
    if (!data) return;

    this.exporterForm?.patchValue({
      nameOrSocialReason: data.exporter?.nameOrSocialReason ?? '',
      fiscalAddress: '',
    });

    this.ducaInfoForm?.patchValue({
      ducaNumber: data.ducaInformation?.ducaNumber ?? '',
      customsOfficeInitial: data.ducaInformation?.customsOfficeInitial ?? '',
      customsOfficeDeparture: data.ducaInformation?.customsOfficeDestination ?? '',
      customsOfficeDestination: data.ducaInformation?.customsOfficeDestination ?? '',
      uniqueReference: '',
    });

    this.transportForm?.patchValue({
      code: data.transport?.code ?? '',
      name: data.transport?.name ?? '',
    });

    this.transporterForm?.patchValue({
      driverFirstAndLastName: data.transporter?.driverFirstAndLastName ?? '',
      transportUnitId: data.transporter?.transportUnitId ?? '',
      trailerIds: data.transporter?.trailerIds ?? [],
      marchamosNumbers: data.transporter?.marchamosNumbers ?? [],
    });

    this.totalValuesForm?.patchValue({
      totalValueInUSD: data.totalValues?.totalValueInUSD ?? 0,
      exchangeRate: 25.92,
      localCurrency: 'HNL',
    });

    this.supportingDocsForm?.patchValue({
      supportingDocumentIds: data.supportingDocuments ?? [],
    });
  }

  public onSave(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this as any).close({ action: 'save', data: null });
  }

  public onClose(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (this as any).close({ action: 'close', data: null });
    this.customsData = null;
  }

  // TRANSPORTER FORM CHIP HELPERS
  get trailerIds(): string[] {
    return this.transporterForm?.controls.trailerIds?.value ?? [];
  }

  addTrailerId(event: string): void {
    const current = [...this.trailerIds];
    if (event === null || event === undefined || event === '') {
      return;
    }
    current.push(event);
    if (this.transporterForm?.controls.trailerIds) {
      this.transporterForm.controls.trailerIds.setValue(current);
    }
  }

  removeTrailerId(index: number): void {
    const current = [...this.trailerIds];
    current.splice(index, 1);
    if (this.transporterForm?.controls.trailerIds) {
      this.transporterForm.controls.trailerIds.setValue(current);
    }
  }

  get marchamosNumbers(): string[] {
    return this.transporterForm?.controls.marchamosNumbers.value ?? [];
  }

  addMarchamo(event: string): void {
    const current = [...this.marchamosNumbers];
    current.push(event);
    if (event === null || event === undefined || event === '') {
      return;
    }
    if (!this.transporterForm?.controls.marchamosNumbers) {
      throw new Error('Marchamos control is not defined in the transporter form.');
    }
    this.transporterForm?.controls.marchamosNumbers.setValue(current);
  }

  removeMarchamo(index: number): void {
    const current = [...this.marchamosNumbers];
    current.splice(index, 1);
    this.transporterForm?.controls.marchamosNumbers.setValue(current);
  }

  setUomLabel(value: number) {
    this.selectedUomLabel = this.setOptionsForPrimeNg(this.unitOfMeasurementEnums).find((uom) => uom.value === value)?.label || '';
  }
}
